/**
 * App lifecycle of the client: the page / app going to the background and back, and the Android back button.
 *
 * Sources: the page's `visibilitychange` (every browser, also a mobile tab and the app's WebView) and, in the native
 * shell, the Capacitor App plugin's `pause` / `resume` / `appStateChange` and `backButton` events. Several sources
 * report the same change: `onPause` / `onResume` run once per change (with the time spent in the background).
 * `onBack` runs for the Android back button only (a browser has none to intercept; Escape keeps its own meaning).
 */
import { nativePlugin } from './capacitor';

export interface LifecycleHandlers {
  /** The app went to the background (screen off, another app, the tab hidden). */
  onPause(): void;
  /** The app is in the foreground again after `hiddenMs` in the background. */
  onResume(hiddenMs: number): void;
  /** The Android back button (native shell only). */
  onBack?(): void;
}

export interface Lifecycle {
  readonly paused: boolean;
  /** Reports a change directly (tests, other shells). */
  setActive(active: boolean): void;
  /** Runs the back handler (tests; the native back button calls it). */
  back(): void;
  dispose(): void;
}

export function installLifecycle(handlers: LifecycleHandlers, win: any = globalThis, now: () => number = () => Date.now()): Lifecycle {
  let paused = false;
  let since = 0;
  let disposed = false;
  const setActive = (active: boolean) => {
    if (disposed || active === !paused) return;
    paused = !active;
    if (paused) {
      since = now();
      handlers.onPause();
    } else handlers.onResume(Math.max(0, now() - since));
  };
  const doc = win?.document;
  const onVisibility = () => setActive(doc.visibilityState !== 'hidden');
  doc?.addEventListener?.('visibilitychange', onVisibility);
  const back = () => {
    if (!disposed) handlers.onBack?.();
  };
  const app = nativePlugin('App', win);
  if (app) {
    app.listen('pause', () => setActive(false));
    app.listen('resume', () => setActive(true));
    app.listen('appStateChange', (s) => {
      if (s && typeof s.isActive === 'boolean') setActive(s.isActive);
    });
    // Listening replaces the WebView's default (history back / closing the app): the game decides.
    app.listen('backButton', back);
  }
  if (doc?.visibilityState === 'hidden') setActive(false);
  return {
    get paused() {
      return paused;
    },
    setActive,
    back,
    dispose() {
      disposed = true;
      doc?.removeEventListener?.('visibilitychange', onVisibility);
    },
  };
}

/** Leaves the app (native shell: App.exitApp); false where a page cannot close itself (a browser tab). */
export function exitApp(win: any = globalThis): boolean {
  const app = nativePlugin('App', win);
  if (!app) return false;
  void app.call('exitApp').catch(() => undefined);
  return true;
}
